import { isNaN } from "lodash-es";
import converter from "number-to-words";

import { UnitType } from "@/types";

export function getFloor(floor: string): number | null {
  const floorInt = parseInt(floor);
  return isNaN(floorInt) || floorInt === 0 ? null : floorInt;
}

export function getBuildingFormatted(
  building: string | null,
  isSelectable: boolean
) {
  if (!building) {
    return null;
  } else {
    return isSelectable ? `Bldg ${building}` : `${building} Bldg`;
  }
}

export function getFloorsFormatted(
  floor: string,
  suffix = "Fl"
): string | null {
  const floorNum = getFloor(floor);
  return floorNum
    ? `${converter.toOrdinal(floorNum)} ${suffix}`
    : floor.length > 0
      ? floor
      : null;
}

export function getSqftFormatted(sqft: number, suffix = "Sqft") {
  return sqft ? `${sqft.toLocaleString()} ${suffix}` : null;
}

export function getBedsFormatted(beds: null | number) {
  return beds ? `${beds} Bd` : beds === 0 ? "Studio" : null;
}
export function getBathsFormatted(baths: null | string) {
  return baths ? `${baths} Ba` : null;
}

export const formatUnitAddress = (unit: UnitType): string => {
  const streetAddress =
    unit.streetAddress1 && unit.streetAddress2
      ? `${unit.streetAddress1 + " " + unit.streetAddress2}`
      : unit.streetAddress1;

  let address = streetAddress;

  if (unit.city && unit.city != "") {
    address = `${address}, ${unit.city}`;
  }

  if (unit.state && unit.state != "") {
    address = `${address}, ${unit.state}`;
  }

  if (unit.zip && unit.zip != "") {
    address = `${address} ${unit.zip}`;
  }

  return address;
};

export function getDisplayRent(unit: UnitType) {
  // If one value exists, but the other doesn't, set the missing value to the other value
  const minMarketingRent = unit?.minMarketingRent ?? unit?.maxMarketingRent;
  const maxMarketingRent = unit?.maxMarketingRent ?? unit?.minMarketingRent;

  return formatRent(minMarketingRent, maxMarketingRent);
}

function isRentValid(
  minMarketingRent: string | null,
  maxMarketingRent: string | null
) {
  return (
    minMarketingRent &&
    minMarketingRent !== "$0.00" &&
    maxMarketingRent &&
    maxMarketingRent !== "$0.00"
  );
}

export function formatRent(
  minMarketingRent: string | null | undefined,
  maxMarketingRent: string | null | undefined
) {
  if (
    minMarketingRent &&
    maxMarketingRent &&
    isRentValid(minMarketingRent, maxMarketingRent)
  ) {
    return minMarketingRent === maxMarketingRent
      ? `${formatCurrency(Number(minMarketingRent))}`
      : `${formatCurrency(Number(minMarketingRent))} - ${formatCurrency(
          Number(maxMarketingRent)
        )}`;
  } else {
    return null;
  }
}

const formatCurrency = (number: number, currency = "USD", locale = "en-US") => {
  return Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency,
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(number);
};

export const numberOfBedrooms = [1, 2, 3, 4, 5].map((element) => ({
  value: element,
  label: `${element}`,
}));
